import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import { Button, Collapse, Form, Message, Space, Tabs, Tag } from '@bedrock/components';
import DescriptionsFormItem from '@mpc/common/src/components/DescriptionsFormItem';
import CollapseTablePanel from '@mpc/common/src/components/CollapseTablePanel';
import classNames from 'classnames/bind';
import {
  useControllableValue,
  useDeepCompareEffect,
  useMemoizedFn,
} from 'ahooks';
import SFormItem from '@mpc/common/src/components/SForm/SFormItem';
import _ from 'lodash';
import EquityFormTable, { getExpectRightsLabel } from '@mpc/common/src/features/EquityFormTable';
import { getFormData, handleFormData } from './details';
import QuoteFileDetail from './QuoteFileDetail';
import styles from './index.less';
import TaxRateInputNumber from './TaxRate';
import DiscountAmount from './DiscountAmount';
import KPITable from '#features/KPITable';
import CooperationPropertyForm from '#features/CooperationFormList/CooperationPropertyForm';
import {
  OutsourceDemandDetailsResDtoDemandSupplierQuoteDto,
  OutsourceDemandDetailsResDto,
  DemandKpiValueDto,
  OutsourceDemandDetailsResDtoAttachmentDto,
} from '#services/api';
import { objectTransenum } from '#components/SForm/SFormElement/utils';
import { currencyEnum } from '#utils/number';
import { MUST_KOL_APPROVE_ID } from '#utils/CONST';
import { preciseFloor } from '#utils/math';
import showAlert from '#utils/showAlert';
import { $sType, getVendorStorage } from '#utils';
import OvertimeTag from '#features/OvertimeTag';
import FormItemDescriptions from '#components/DescriptionsFormItem';
import { orderNum } from '#utils/number';
import ActualExecutionTag from '#features/ActualExecutionTag';
import CooperationDetails from '#features/CooperationFormList/CooperationDetails';
import PriceDetailModal from '#features/CooperationFormList/PriceDetailModal';
import { AiTag, CurrencyType } from '#components';
import TravelTag from '#features/TravelTag';
import { useDemandOptions } from '#features/useDemandOptions';
import { enums } from '#services';
import { ChevronRight } from '@bedrock/icons-react';
import ClosingReportBtn from '#features/MPCDemand/ClosingReportTable/ClosingReportBtn';
import AuditException from '#features/AuditException';
import { bindAiReviewResultToFormItem } from '#features/MPCDemand';
import BankAccount from '#features/BankAccount';

const cx = classNames.bind(styles);

type QuotNode = {
  $last_round_price?: {
    value: number;
    currency: string;
  };
} & OutsourceDemandDetailsResDtoDemandSupplierQuoteDto;

const INVOICEOption = enums.InvoiceBelongAreaEnum.values()

type QuotValue = {
  demand_supplier_quotes?: QuotNode[];
  last_round_quotes?: QuotNode[];
  common_kpi_values?: DemandKpiValueDto[];
  /** 报价类型:0=不含税金额；1=含税金额 */
  quote_type?: number;
  /** 税率 */
  tax_rate?: number;
  /** 是否收取服务费,0=不收取,1=收取 */
  service_fee_mode?: number;
  /** 服务费率 */
  service_fee_rate?: number;
  // 报价币种
  currency?: string;
  // 上轮报价总金额
  $last_round_total?: {
    value: number;
    currency: string;
  };
};

type NewPendingQuoteProps = {
  detail?: OutsourceDemandDetailsResDto & {
    /** 三级单 */
    $thirdLevelOrder?: boolean
  };
  /** type 目前用来区分使用的地方
   * pendingQuote-外包待报价bottom
   * making-制作中bottom的变更报价tab
   */
  type?: string;
  value?: QuotValue;
  defaultValue?: QuotValue;
  view?: boolean;
  onChange?: (value: QuotValue) => void;
  showLastRound?: boolean;
  onlyWinning?: boolean;
  supplierQuotationTableConfig?: any;
  /** 合作内容以标tab模式展示 */
  tabMode?: boolean;
};

const getTotalQuote = (
  calculate: [{ total_price_quoted: { value: number | string } }],
  key: 'total_price_quoted' | '$last_round_price'
) => {
  let totalQuoted = calculate?.reduce?.((prev, curr) => {
    const currValue = Number(curr?.[key]?.value || 0);
    return _.add(prev, Number(currValue));
  }, 0);

  totalQuoted = preciseFloor(totalQuoted, 2);

  return totalQuoted;
};

// 基于total_price更新报价的独立函数
function updateQuotePrices(
  item: any,
  attachment: {
    total_price?: number;
  } & OutsourceDemandDetailsResDtoAttachmentDto,
  isQuoteTypeWithTax: boolean
) {
  if (isQuoteTypeWithTax) {
    return {
      ...item,
      quote_price_with_tax: {
        ...item.quote_price_with_tax,
        // 优惠前，价格以报价附件的价格优先
        value:
          attachment?.total_price || item?.quote_price_with_tax?.value || '',
      },
      discount_quote_price_with_tax: {
        ...item.discount_quote_price_with_tax,
        // 优惠后，提交报价明细表后，同步更新【优惠后】金额, #59456
        value:
          attachment?.total_price ||
          item?.discount_quote_price_with_tax?.value ||
          '',
      },
      quote_contain_ai: attachment?.quote_contain_ai,
    };
  } else {
    return {
      ...item,
      quote_price_no_tax: {
        ...item.quote_price_no_tax,
        // 优惠前，价格以报价附件的价格优先
        value: attachment?.total_price || item?.quote_price_no_tax?.value || '',
      },
      discount_quote_price_no_tax: {
        ...item.discount_quote_price_no_tax,
        // 优惠后，价格以填写价格优先
        value:
          item?.discount_quote_price_no_tax?.value ||
          attachment?.total_price ||
          '',
      },
      quote_contain_ai: attachment?.quote_contain_ai,
    };
  }
}

const currencyOptions = objectTransenum(currencyEnum);

/** 此组件被使用的地方
 * 外包待报价Bottom、确认报价bottom、制作中bottom的变更报价tab、详情中的报价结算信息
 * 内包重新报价bottomTab(很老的旧数据)、历史报价Modal、详情三级单中的报价汇总
 */
const NewPendingQuote = forwardRef((props: NewPendingQuoteProps, ref) => {
  const isInner = $sType === 'inner';
  const {
    detail = {},
    view,
    type = '',
    showLastRound = false,
    onlyWinning = false,
    supplierQuotationTableConfig,
    tabMode = false,
  } = props;
  const [form] = Form.useForm();
  const [detailValue, setDetailValue] = useControllableValue<QuotValue>(props, {
    defaultValue: {
      demand_supplier_quotes: [],
    },
  });

  const [localData, setLocalData] = useState(null);
  const [isShowTax, setIsShowTax] = useState(false);
  const [rowSpan, setRowSpan] = useState(0);
  const [lastRoundQuotes, setLastRoundQuotes] = useState([]);
  const sVersion = detail?.structured_version ?? 0;
  const [activeTabKey, setActiveTabKey] = useState('0');

  const hasChangeQuote = !!detail.remark;

  const demandAndCooperate = useMemo(() => {
    return {
      cooperate_accounts: detail?.demand_plan_info?.cooperate_accounts || [],
      common_kpi_values: detail?.demand_plan_info?.common_kpi_values || [],
    };
  }, [detail]);

  const { optionsMap } = useDemandOptions({
    keys: ['invoice_type'],
    params: {
      is_oversea_invoice: localData?.is_oversea_invoice ?? 1,
    },
    cache: false,
  });
  useImperativeHandle(
    ref,
    () => {
      return {
        validateFields: () => {
          const data = form.getFieldsValue();
          if (detail.is_bind_execution_data) {
            // 绑定执行数据校验
            const someInput = data?.demand_supplier_quotes?.some((n) => {
              return !!n.program_desc;
            });
            // 至少有一个报价内容填写
            if (!someInput) {
              showAlert({
                message: '请填写至少一个合作内容的报价方案',
                type: 'error',
              });
              return Promise.reject('请填写至少一个合作内容的报价方案');
            }
          } else {
            // 整单报价校验：所有合作内容都必须填写报价金额-优惠前
            if (detail?.need_all_quote) {
              const emptyIndexes: number[] = [];
              (data?.demand_supplier_quotes || []).forEach((n: any, idx: number) => {
                if (!n?.quote_price_with_tax?.value) {
                  emptyIndexes.push(idx);
                }
              });
              if (emptyIndexes.length > 0) {
                const cooperateAccounts = detail?.demand_plan_info?.cooperate_accounts || [];
                const emptyLabels = emptyIndexes.map((idx) => {
                  const account = cooperateAccounts[idx];
                  return `合作内容${orderNum(account?.$orderIndex, idx)}`;
                });
                const message = `此单要求整单需全部报价，${emptyLabels.join('、')}未报价，请补充`;
                showAlert({ message, type: 'error' });
                return Promise.reject(message);
              }
            }

            const someInput = data?.demand_supplier_quotes?.some((n) => {
              // 其他类型，至少有一个报价价格，优惠金额大于0，报价附件需要添加
              return (
                !!n?.total_price_quoted?.value &&
                n?.discount_amount?.value >= 0 &&
                n?.quote_attachments?.length > 0
              );
            });
            // 至少有一个报价内容填写
            if (!someInput) {
              showAlert({
                message:
                  '请填写至少完善一个合作内容的报价，包括报价明细附件,且优惠金额大于等0',
                type: 'error',
              });
              return Promise.reject('请填写至少完善一个合作内容的报价');
            }

            // 全部填写或者全部为空
            const isCompleteOrEmpty = data?.demand_supplier_quotes?.every(
              (n) => {
                const checkKeys = [
                  'quote_price_with_tax.value',
                  'quote_price_with_tax.value',
                  'discount_quote_price_with_tax.value',
                  'quote_attachments.length',
                ];
                // 全部填写,且优惠金额大于0
                if (
                  checkKeys.every((key) => !!_.get(n, key)) &&
                  n?.discount_amount?.value >= 0
                ) {
                  return true;
                }
                // 全部为空
                if (checkKeys.every((key) => !_.get(n, key))) {
                  return true;
                }
                return false;
              }
            );
            if (!isCompleteOrEmpty) {
              showAlert({
                message: '如果填写报价，请完善报价或者完全清空',
                type: 'error',
              });
              return Promise.reject('如果填写报价，请完善报价或者完全清空');
            }
          }
          return form.validateFields();
        },
        getFieldsValue: form.getFieldsValue,
      };
    },
    [form, detail]
  );

  useEffect(() => {
    if (!_.isEqual(detailValue, localData)) {
      let newFormValue = { ...detailValue };
      // 外包重新报价场景，以本地数据为准
      // 外包重新报价没有明确字段区分
      if (view && localData && $sType === 'outer' && showLastRound) {
        newFormValue = { ...localData };
      }
      let quotes = [];
      if (view || type === 'making') {
        // 非编辑状态或者结案状态，以报价数据为准
        detailValue?.demand_supplier_quotes?.forEach((item) => {
          const cooperate_account = demandAndCooperate?.cooperate_accounts.find(
            (n) => n.id === item.cooperate_account_id
          );
          if (cooperate_account) {
            item.cooperate_account = cooperate_account;
            item.cooperate_account_id = cooperate_account.id;
          }
        });
        // TODO: 是否生效？？?,@yangtianyu01
        // if (onlyWinning) {
        //     detailValue.demand_supplier_quotes = detailValue?.demand_supplier_quotes?.filter(n => n?.winning) || [];
        // }
      } else {
        // 新建报价或确认报价修改状态，以合作内容为准
        quotes = detailValue?.demand_supplier_quotes;
        const old_demand_supplier_quotes = quotes || [];
        newFormValue.demand_supplier_quotes =
          demandAndCooperate?.cooperate_accounts.map((n, i) => {
            let old_quote_config = old_demand_supplier_quotes.find(
              (i) => i.cooperate_account_id === n.id
            );
            return {
              cooperate_account: n,
              cooperate_account_id: n.id,
              ...old_quote_config,
            };
          });
      }

      newFormValue.common_kpi_values = demandAndCooperate?.common_kpi_values;
      let result = getFormData(newFormValue);

      if (sVersion >= 1) {
        // 新报价模板，从详情中获取报价类型
        result.quote_type = detail?.demand_suppliers?.[0]?.quote_type ?? 1;
      }

      if (result?.service_fee_mode === 1) {
        setRowSpan(1);
      } else {
        setRowSpan(2);
      }

      if (result?.quote_type) {
        setIsShowTax(true);
        result = notTaxCalculateAmount(result, {
          service_fee_rate: result.service_fee_rate,
          tax_rate: result.tax_rate,
        });
      } else {
        setIsShowTax(false);
        result = taxCalculateAmount(result, {
          service_fee_rate: result.service_fee_rate,
          tax_rate: result.tax_rate,
        });
      }
      result.remark = result.remark || detail?.remark;
      result.currency =
        result?.demand_supplier_quotes?.[0]?.currency?.toLocaleLowerCase() ||
        'rmb';
      form.setFieldsValue(result);
    }
  }, [detailValue, localData, demandAndCooperate, view]);

  const handleChangeForm = useMemoizedFn(
    (changedValues: any, allValues: any) => {
      let calculaResult;
      const { service_fee_rate, tax_rate, service_fee_mode, quote_type } =
        allValues || {};

      setRowSpan(service_fee_mode === 1 ? 1 : 2);

      // 总报价明细上传后生成报价明细分配至各个合作内容
      if (changedValues.merge_quote_attachs && allValues) {
        const merge_quote_attachs = changedValues.merge_quote_attachs;
        allValues = {
          ...allValues,
          demand_supplier_quotes: allValues?.demand_supplier_quotes?.map(
            (item) => {
              const attachment =
                merge_quote_attachs?.[0]?.allQuoteAttachData?.[
                  item.cooperate_account_id
                ];
              // 如果该合作内容在报价明细提交结果中找不到对应数据（已被删除），清空金额
              if (!attachment) {
                const clearedItem = { ...item };
                if (clearedItem.quote_price_with_tax) {
                  clearedItem.quote_price_with_tax = {
                    ...clearedItem.quote_price_with_tax,
                    value: '',
                  };
                }
                if (clearedItem.quote_price_no_tax) {
                  clearedItem.quote_price_no_tax = {
                    ...clearedItem.quote_price_no_tax,
                    value: '',
                  };
                }
                if (clearedItem.discount_quote_price_with_tax) {
                  clearedItem.discount_quote_price_with_tax = {
                    ...clearedItem.discount_quote_price_with_tax,
                    value: '',
                  };
                }
                if (clearedItem.discount_quote_price_no_tax) {
                  clearedItem.discount_quote_price_no_tax = {
                    ...clearedItem.discount_quote_price_no_tax,
                    value: '',
                  };
                }
                clearedItem.quote_attachments = [];
                clearedItem.quote_attachs_blob_keys = [];
                return clearedItem;
              }
              const attachments = attachment
                ? [
                    {
                      ...attachment,
                      id: attachment?.blob_id,
                      blob: { ...attachment, id: attachment?.blob_id },
                      total_price: null,
                    },
                  ]
                : [];

              const updatedItem = updateQuotePrices(
                item,
                attachment,
                !!allValues?.quote_type
              );

              return {
                ...updatedItem,
                quote_attachments: attachments,
                quote_attachs_blob_keys: attachments?.map(
                  (v) => v?.blob?.key || v?.blob?.blob_key
                ),
              };
            }
          ),
        };
      }

      // 单个报价附件变更，基于报价附件的total_price更新报价
      if (changedValues.demand_supplier_quotes && allValues) {
        const demand_supplier_quotes = changedValues.demand_supplier_quotes;
        allValues = {
          ...allValues,
          demand_supplier_quotes: allValues?.demand_supplier_quotes?.map(
            (item, index) => {
              const attachment =
                demand_supplier_quotes?.[index]?.quote_attachments?.[0];
              if (attachment?.data?.ext_data?.total_price) {
                attachment.total_price =
                  attachment?.data?.ext_data?.total_price;
                const attachments = [
                  {
                    ...attachment,
                    data: {
                      ...attachment?.data,
                      ext_data: {
                        ...attachment?.data?.ext_data,
                        total_price: null, // 重置报价明细的total_price，避免下次变更时，total_price被覆盖
                      },
                    },
                  },
                ];

                const updatedItem = updateQuotePrices(
                  item,
                  attachment,
                  !!allValues?.quote_type
                );

                return {
                  ...updatedItem,
                  quote_attachments: attachments,
                  quote_attachs_blob_keys: attachments?.map(
                    (v) => v?.blob?.key || v?.blob?.blob_key
                  ),
                };
              } else {
                return item;
              }
            }
          ),
        };
      }

      if (changedValues.invoice_belong_area !== undefined) {
        if (changedValues.invoice_belong_area !== 1) {
          allValues.is_oversea_invoice = 1;
          allValues.invoice_type = '';
        } else {
          allValues.is_oversea_invoice = 0;
          allValues.invoice_type = '';
        }
      }

      if (changedValues.is_oversea_invoice !== undefined) {
        allValues.invoice_type = '';
      }

      if (quote_type) {
        setIsShowTax(true);
        calculaResult = notTaxCalculateAmount(allValues, {
          service_fee_rate,
          tax_rate,
        });
      } else {
        setIsShowTax(false);
        calculaResult = taxCalculateAmount(allValues, {
          service_fee_rate,
          tax_rate,
        });
      }

      form.setFieldsValue(calculaResult);
      const result = handleFormData(calculaResult, detail);
      setDetailValue(result);
      setLocalData(result);
    }
  );
  const isDOMESTICProduct = detail?.product_area === 'DOMESTIC';
  const quotationSummaryFormItem = [
    {
      key: 'channel_supplier_bank_dto',
      label: '收款账户',
      formItemKey: 'channel_supplier_bank_dto',
      required: isDOMESTICProduct,
      show: detail?.state?.toLowerCase?.() === 'pending_quote',
      view: view,
      span: 1,
      watchChildFormValidate: true,
      render: () => <BankAccount mdm_code={getVendorStorage()?.vendorId} view={view} showAddBank required={isDOMESTICProduct} />
    },
    {
      key: 'currency',
      label: '报价币种 ',
      formItemKey: 'currency',
      view: view || type === 'making',
      componentClass: 'select',
      required: true,
      span: 1,
      elementProps: {
        style: {
          width: view || type === 'making' ? '100%' : '40%',
        },
        options: currencyOptions,
      },
    },
    {
      key: 'quote_type',
      label: '报价类型',
      formItemKey: 'quote_type',
      componentClass: 'radio',
      view: view || type === 'making' || sVersion >= 1,
      required: true,
      elementProps: {
        style: {
          marginTop: view || type === 'making' ? '0px' : '5px',
        },
        options: [
          {
            name: '含税金额',
            value: 1,
          },
          {
            name: '不含税金额',
            value: 0,
          },
        ],
      },
    },
    {
      key: 'tax_rate',
      label: '税率',
      formItemKey: 'tax_rate',
      view: view || type === 'making',
      componentClass: 'inputNumber',
      span: 1,
      required: true,
      render: () => {
        return (
          <TaxRateInputNumber
            style={{ width: detail?.is_bind_execution_data ? '40%' : '100%' }}
            view={view || type === 'making'}
          />
        );
      },
    },
    {
      key: 'service_fee_mode',
      label: '是否收取服务费',
      formItemKey: 'service_fee_mode',
      componentClass: 'radio',
      view: view || type === 'making',
      required: true,
      span: rowSpan,
      elementProps: {
        style: {
          marginTop: view || type === 'making' ? '0px' : '5px',
        },
        options: [
          {
            name: '是',
            value: 1,
          },
          {
            name: '否',
            value: 0,
          },
        ],
      },
    },
    {
      key: 'service_fee_rate',
      label: '服务费率',
      formItemKey: 'service_fee_rate',
      componentClass: 'inputNumber',
      view: view || type === 'making',
      required: true,
      render: () => {
        return <TaxRateInputNumber view={view || type === 'making'} />;
      },
    },
    {
      key: 'invoice_belong_area',
      label: '发票国家/地区',
      span: 1,
      formItemKey: 'invoice_belong_area',
      componentClass: 'select',
      view: view || type === 'making',
      required: true,
      elementProps: {
        style: {
          // width: view || type === 'making' ? '100%' : '40%',
        },
        options: INVOICEOption,
      },
      show: !detail?.$thirdLevelOrder,
    },
    {
      key: 'is_oversea_invoice',
      label: '是否海外INVOICE',
      formItemKey: 'is_oversea_invoice',
      componentClass: 'radio',
      view: view || type === 'making',
      required: true,
      span: 1,
      elementProps: {
        style: {
          marginTop: view || type === 'making' ? '0px' : '5px',
        },
        disabled: localData?.invoice_belong_area !== 1,
        options: [
          {
            name: '是',
            value: 1,
          },
          {
            name: '否',
            value: 0,
          },
        ],
      },
      show: !detail?.$thirdLevelOrder,
    },
    {
      key: 'invoice_type',
      label: '发票类型',
      span: 2,
      formItemKey: 'invoice_type',
      componentClass: 'select',
      view: view || type === 'making',
      required: true,
      elementProps: {
        style: {
          width: view || type === 'making' ? '100%' : '40%',
        },
        options: optionsMap?.invoice_type?.options,
      },
      show: !detail?.$thirdLevelOrder,
    },
    {
      key: '$last_round_total',
      label: '报价总金额-变更前(含税 + 服务费)',
      formItemKey: '$last_round_total',
      className: cx('quote_price'),
      componentClass: 'currencyType',
      required: true,
      span: 1,
      view: true,
      show: !!showLastRound && hasChangeQuote,
    },
    {
      key: 'total_quoted',
      label: `报价总金额${showLastRound && hasChangeQuote ? '-变更后\n' : ''}(含税 + 服务费)`,
      formItemKey: 'total_quoted',
      componentClass: 'currencyType',
      required: true,
      span: showLastRound && hasChangeQuote ? 1 : 2,
      view: true,
    },
    {
      key: 'remark',
      label: '报价变更原因',
      required: true,
      formItemKey: 'remark',
      componentClass: 'textarea',
      span: 2,
      view,
      // 只有在变更报价时和详情时才显示
      show: (showLastRound && hasChangeQuote) || type === 'making',
    },
    {
      key: 'merge_quote_attachs',
      label: '报价总明细',
      formItemKey: 'merge_quote_attachs',
      required: !view,
      span: type === 'pendingQuote' ? 2 : 1,
      show:
        (view || detail?.quote_upload_method === 'MULTI_UPLOAD') &&
        !detail?.$isAdsXiaohao,
      render: () => {
        return <QuoteFileDetail formData={detailValue} detail={detail} view={view} isAll supplierQuotationTableConfig={supplierQuotationTableConfig} />;
      },
    },
    {
      key: 'quote_settle_reports',
      label: '结案明细',
      formItemKey: 'quote_settle_reports',
      span: 2,
      show: (detail?.depth === 2) && detail.need_structured_settle_report && view && $sType === 'outer',
      render: () => {
        return <ClosingReportBtn detail={detail} view={true} />;
      },
    },
    {
      key: '$settle_price',
      label: '最终结算总金额(供应商)',
      required: true,
      formItemKey: '$settle_price',
      componentClass: 'currencyType',
      span: 1,
      view: true,
      show:
        !!view &&
        !!detail?.need_cooperate_account_supplier_settle_price &&
        type !== 'making',
    },
    {
      key: 'bid_attachs',
      label: '供应商比选方案及文件',
      className: view ? cx('view_upload') : cx('attach_upload'),
      formItemKey: 'bid_attachs',
      componentClass: 'upload',
      required: false,
      span: 2,
      view,
      elementProps: {
        tip: '单个文件不能超过50M',
        fileSizeLimit: 50 * 1024 * 1024,
      },
    },
    {
      formItemKey: 'common_kpi_values',
      key: 'common_kpi_values',
      label: '指标',
      required: false,
      span: 2,
      watchChildFormValidate: true,
      validateTrigger: false,
      render: (renderData) => {
        return (
          <KPITable
            view={true}
            required={false}
            platforms={['MPC']}
            detailState={props.detail?.state}
          />
        );
      },
    },
  ].filter((n) => n.show !== false);

  const getCooperationContentFormItem = useMemoizedFn(
    (keyConfig, index_id, demand_supplier_quote, cooperationAccount) => {
      const showMergeQuote =
        !!detail?.need_cooperate_account_supplier_settle_price &&
        !['making', 'pendingQuote'].includes(type);

      const expectRightsKey =
        $sType === 'inner' && detail?.depth === 2
          ? 'supplier_expect_rights_list'
          : 'expect_rights';

      return [
        {
          key: ['cooperate_account', 'property_values'],
          label: '合作内容',
          span: 2,
          render: () => {
            return <CooperationDetails index_id={index_id} need_ai={cooperationAccount?.need_ai} />;
          },
        },
        {
          key: ['cooperate_account', 'kpi_values'],
          label: '考核指标',
          required: false,
          span: 2,
          watchChildFormValidate: true,
          validateTrigger: false,
          show: detail?.need_cooperate_account_supplier_settle_price
            ? demand_supplier_quote?.need_actual_execution
            : true,
          render: (renderData) => {
            return (
              <KPITable
                view={true}
                required={false}
                platforms={['MPC']}
                detailState={props.detail?.state}
                structuredVersion={detail?.structured_version}
                emptyRender={() => {
                  return <div>--</div>;
                }}
              />
            );
          },
        },
        {
          key: ['cooperate_account', 'necessary_rights'],
          label: '必要权益',
          span: 2,
          show: detail?.structured_version >= 1 && demand_supplier_quote?.need_actual_execution,
          render: () => {
            return (
              <EquityFormTable
                formItemKey="necessary_rights"
                detail={detail}
                view
              />
            );
          },
        },
        {
          key: ['cooperate_account', expectRightsKey],
          label: getExpectRightsLabel(expectRightsKey),
          span: 2,
          show: detail?.structured_version >= 1 && demand_supplier_quote?.need_actual_execution,
          watchChildFormValidate: true,
          validateTrigger: false,
          render: () => {
            return (
              <EquityFormTable
                formItemKey={expectRightsKey}
                detail={detail}
                view={view || type === 'making'}
              />
            );
          },
        },
        {
          key: ['cooperate_account', 'remark'],
          label: '备注',
          componentClass: 'textarea',
          span: 2,
          view: true,
        },
        {
          key: 'program_desc',
          label: '报价方案',
          formItemKey: 'program_desc',
          componentClass: 'textarea',
          required: false,
          view,
          span: 2,
        },
        {
          key: 'quote_price_no_tax',
          label: '报价金额-优惠前(不含税)',
          formItemKey: 'quote_price_no_tax',
          className: cx('quote_price'),
          componentClass: 'currencyType',
          required: false,
          view: view || isShowTax,
          elementProps: {
            selectDisabled: true,
          },
          show: !showMergeQuote,
        },
        {
          key: 'quote_price_with_tax',
          label: '报价金额-优惠前(含税 + 服务费)',
          className: cx('quote_price'),
          formItemKey: 'quote_price_with_tax',
          componentClass: 'currencyType',
          required: Boolean(detail?.need_all_quote),
          view: view || !isShowTax,
          elementProps: {
            selectDisabled: true,
          },
          show: !showMergeQuote,
        },
        {
          key: 'discount_amount',
          label: '优惠金额(含税 + 服务费)',
          className: cx('quote_price'),
          formItemKey: 'discount_amount',
          required: false,
          span: 2,
          render: () => {
            return <DiscountAmount />;
          },
          show: !showMergeQuote,
        },
        {
          key: 'discount_quote_price_no_tax',
          label: '报价金额-优惠后(不含税)',
          className: cx('quote_price'),
          formItemKey: 'discount_quote_price_no_tax',
          componentClass: 'currencyType',
          required: false,
          view: view || isShowTax,
          elementProps: {
            selectDisabled: true,
          },
          show: !showMergeQuote,
        },
        {
          key: 'discount_quote_price_with_tax',
          label: '报价金额-优惠后(含税 + 服务费)',
          className: cx('quote_price'),
          formItemKey: 'discount_quote_price_with_tax',
          componentClass: 'currencyType',
          required: false,
          view: view || !isShowTax,
          elementProps: {
            selectDisabled: true,
          },
          show: !showMergeQuote,
        },
        {
          key: '$last_round_price',
          label: '报价总金额-变更前\n(含税 + 服务费)',
          formItemKey: '$last_round_price',
          className: cx('quote_price'),
          componentClass: 'currencyType',
          required: false,
          view: true,
          show: showLastRound && !showMergeQuote,
          span: 1,
        },
        {
          key: 'total_price_quoted',
          label: `报价总金额${showLastRound ? '-变更后\n' : ''}(含税 + 服务费)`,
          formItemKey: 'total_price_quoted',
          componentClass: 'currencyType',
          required: false,
          view: true,
          span: showLastRound ? 1 : 2,
          show: !showMergeQuote,
        },
        {
          key: 'quote_attachments',
          label: '报价明细',
          formItemKey: 'quote_attachments',
          required: false,
          span: 2,
          render: () => {
            const config = keyConfig('quote_attachments');
            const quoteAttachmentsView =
              view || detail.quote_upload_method === 'MULTI_UPLOAD';
            return (
              <QuoteFileDetail
                detail={detail}
                view={quoteAttachmentsView}
                quoteIndex={config.fieldKey || 0}
                supplierQuotationTableConfig={supplierQuotationTableConfig}
              />
            );
          },
          show: !showMergeQuote,
        },
        {
          key: 'discount_quote_price_with_tax',
          label: '报价金额-变更后(含税 + 服务费)',
          span: 1,
          show: showMergeQuote,
          className: cx('quote_price'),
          render: () => {
            return (
              <PriceDetailModal
                demand_supplier_quote={demand_supplier_quote}
                detail={detail}
                orderNum={index_id}
              />
            );
          },
        },
        {
          key: 'supplier_settle_price',
          label: '最终结算金额',
          span: 1,
          show: showMergeQuote,
          componentClass: 'currencyType',
          view: true,
        },
        {
          key: 'case_close_detail',
          label: '结案明细',
          span: 2,
          show: (detail?.depth === 2) && detail?.need_structured_settle_report && demand_supplier_quote?.need_actual_execution,
          view: true,
          elementProps: ({ optionMap, detailData }) => {
              return {
                  showComponent: (value) => {
                      return (
                          <ClosingReportBtn detail={detail} view={true} cooperateAccountId={demand_supplier_quote?.cooperate_account_id}/>
                      )
                  }
              }
          },
          ...bindAiReviewResultToFormItem(`quoteSettleReport:${index_id}`, {
            detailData: detail,
        }),
        },
        {
          key: 'case_close_report_cooperate_account_attachments',
          label: '结案报告 (差旅、餐补)',
          span: 2,
          view: true,
          show: detail?.depth === 2 && 
            detail?.$contentStructured && 
            !!demand_supplier_quote?.need_actual_execution &&
            !!detail?.is_screenshot_done,
          componentClass: 'upload',
          ...bindAiReviewResultToFormItem(`caseCloseReportCooperateAccountAttachments:${index_id}`, {
            detailData: detail,
          }, { showGroups: true }),
        },
      ]
        .filter((n) => n.show !== false)
        .map((n) => {
          return {
            ...keyConfig(n.key),
            ...n,
          };
        });
    }
  );

  const notTaxCalculateAmount = useMemoizedFn(
    (formValue: any, { service_fee_rate = 0, tax_rate = 0 }) => {
      const currency =
        formValue?.currency ||
        formValue?.demand_supplier_quotes?.[0]?.currency?.toLocaleLowerCase();
      const rate = (1 + tax_rate) * (1 + service_fee_rate);
      const calculate = formValue.demand_supplier_quotes?.map((item) => {
        const returnData = {
          ...item,
          cooperate_account: item.cooperate_account,
          cooperate_account_id: item.cooperate_account_id,
          program_desc: item.program_desc,
          // 报价金额-优惠前(不含税)
          quote_price_no_tax: {
            value: preciseFloor(item.quote_price_with_tax?.value / rate, 2),
            currency,
          },
          // 报价金额-优惠后(不含税)
          discount_quote_price_no_tax: {
            value: preciseFloor(
              item.discount_quote_price_with_tax?.value / rate,
              2
            ),
            currency,
          },
          // 报价金额-优惠后(含税 + 服务费)
          discount_quote_price_with_tax: {
            value: item.discount_quote_price_with_tax?.value || '',
            currency,
          },
          // 报价金额-优惠前(含税 + 服务费)
          quote_price_with_tax: {
            value: item.quote_price_with_tax?.value || '',
            currency,
          },
          // 优惠金额(含税 + 服务费)
          discount_amount: {
            value: preciseFloor(
              item.quote_price_with_tax?.value -
                item.discount_quote_price_with_tax?.value,
              2
            ),
            currency,
          },
          // 报价总金额(含税 + 服务费)
          total_price_quoted: {
            value: item?.discount_quote_price_with_tax?.value,
            currency,
          },
          // 最终结算金额 (供应商提交的最终结算金额)
          supplier_settle_price: {
            value:
              typeof item?.supplier_settle_price === 'object'
                ? item?.supplier_settle_price?.value
                : item?.supplier_settle_price,
            currency,
          },
          quote_attachments: item.quote_attachments,
          quote_attachs_blob_keys: item.quote_attachments?.map(
            (item) => item?.blob?.blob_key
          ),
        };
        // 变更报价总金额金额
        if (type === 'making') {
          returnData.requote_price = returnData?.total_price_quoted;
        }
        return returnData;
      });

      return {
        ...formValue,
        total_quoted: {
          value: getTotalQuote(calculate, 'total_price_quoted'),
          currency,
        },
        $last_round_total: {
          value: getTotalQuote(calculate, '$last_round_price'),
          currency,
        },
        $settle_price: {
          value: detail?.cooperate_account_supplier_settle_price_sum,
          currency,
        },
        demand_supplier_quotes: calculate,
      };
    }
  );

  const taxCalculateAmount = useMemoizedFn(
    (formValue: any, { service_fee_rate = 0, tax_rate = 0 }) => {
      const currency =
        formValue?.currency ||
        formValue?.demand_supplier_quotes?.[0]?.currency?.toLocaleLowerCase();
      const rate = (1 + tax_rate) * (1 + service_fee_rate);
      const calculate =
        formValue.demand_supplier_quotes?.map((item) => {
          const returnData = {
            ...item,
            cooperate_account: item.cooperate_account,
            cooperate_account_id: item.cooperate_account_id,
            program_desc: item.program_desc,
            quote_attachments: item.quote_attachments,
            quote_attachs_blob_keys: item.quote_attachments?.map(
              (item) => item?.blob?.blob_key
            ),
            // 报价金额-优惠前(不含税)
            quote_price_no_tax: {
              value: item.quote_price_no_tax?.value || '',
              currency,
            },
            // 报价金额-优惠后(不含税)
            discount_quote_price_no_tax: {
              value: item.discount_quote_price_no_tax?.value || '',
              currency,
            },
            // 报价金额-优惠前(含税 + 服务费)
            quote_price_with_tax: {
              value: preciseFloor(item.quote_price_no_tax?.value * rate, 2),
              currency,
            },
            // 报价金额-优惠后(含税 + 服务费)
            discount_quote_price_with_tax: {
              value: preciseFloor(
                item.discount_quote_price_no_tax?.value * rate,
                2
              ),
              currency,
            },
            // 优惠金额(含税 + 服务费)
            discount_amount: {
              value: preciseFloor(
                item.quote_price_no_tax?.value * rate -
                  item.discount_quote_price_no_tax?.value * rate,
                2
              ),
              currency,
            },
            // 报价总金额(含税 + 服务费)
            total_price_quoted: {
              value: preciseFloor(
                item.discount_quote_price_no_tax?.value * rate,
                2
              ),
              currency,
            },
          };
          // 变更报价总金额金额
          if (type === 'making') {
            returnData.requote_price = returnData?.total_price_quoted;
          }
          return returnData;
        }) || [];

      return {
        ...formValue,
        total_quoted: {
          value: getTotalQuote(calculate, 'total_price_quoted'),
          currency: currency,
        },
        $last_round_total: {
          value: getTotalQuote(calculate, '$last_round_price'),
          currency,
        },
        $settle_price: {
          value: detail?.cooperate_account_supplier_settle_price_sum,
          currency,
        },
        demand_supplier_quotes: calculate,
      };
    }
  );

  const accounts = props.detail?.demand_plan_info?.cooperate_accounts || [];
  const isKol = useMemo(() => {
    return MUST_KOL_APPROVE_ID.find((n) =>
      detail?.l3_category_id?.toString()?.startsWith(n)
    );
  }, [detail]);

  const items = useMemo(() => {
    return quotationSummaryFormItem.filter((item) => {
      // 绑定执行数据
      const bindExecution = [
        'channel_supplier_bank_dto',
        'tax_rate',
        'service_fee_mode',
        'invoice_belong_area',
        'is_oversea_invoice',
        'invoice_type',
        'remark',
        'bid_attachs',
      ];
      // 通用需求类型
      const basicDemand = [
        'channel_supplier_bank_dto',
        'quote_type',
        'currency',
        'tax_rate',
        'service_fee_mode',
        'invoice_belong_area',
        'is_oversea_invoice',
        'invoice_type',
        '$last_round_total',
        'total_quoted',
        'remark',
        '$settle_price',
        'bid_attachs',
        'merge_quote_attachs',
        'quote_settle_reports',
      ];
      // 详情变更
      const detailChange = [
        'channel_supplier_bank_dto',
        'quote_type',
        'tax_rate',
        'currency',
        'service_fee_mode',
        'invoice_belong_area',
        'is_oversea_invoice',
        'invoice_type',
        '$last_round_total',
        'total_quoted',
        'remark',
        '$settle_price',
        'bid_attachs',
        'merge_quote_attachs',
        'quote_settle_reports',
      ];

      if (isKol) {
        basicDemand.push('common_kpi_values');
      }
      if (detailValue?.service_fee_mode) {
        basicDemand.splice(3, 0, 'service_fee_rate');
        bindExecution.splice(3, 0, 'service_fee_rate');
        detailChange.splice(3, 0, 'service_fee_rate');
      } else {
        basicDemand.splice(3, 0);
        bindExecution.splice(3, 0);
        detailChange.splice(3, 0);
      }
      if (detail.is_bind_execution_data) {
        return bindExecution.includes(item.formItemKey);
      }
      if (
        ['making'].includes(detail?.state) ||
        ['s_immediately_bidding'].includes(detail?.purchasing_method)
      ) {
        return detailChange.includes(item.formItemKey);
      }
      return basicDemand.includes(item.formItemKey);
    });
  }, [detailValue, isKol, rowSpan, quotationSummaryFormItem]);

  const [lastRoundValue, setLastRoundValue] = useState<QuotValue>({
    $last_round_total: {
      value: 0,
      currency: 'rmb',
    },
    demand_supplier_quotes: [],
  });
  // 设置上一轮报价
  const mergeLastRoundQuotes = useMemoizedFn(() => {
    const newQuotes = detailValue?.demand_supplier_quotes || [];
    newQuotes.forEach((item, index) => {
      const $last_round_price = {
        value: item?.last_quote_price || item?.quote_price,
        currency: item?.currency,
      };
      // 如果编辑且显示上一轮报价，则使用当前报价固定值
      if (['making'].includes(type)) {
        $last_round_price.value = item?.quote_price;
      }

      if (item) {
        newQuotes[index] = {
          ...item,
          $last_round_price: $last_round_price,
        };
      }
    });
    const lastRoundTotal = newQuotes.reduce((pre, acc) => {
      return _.add(pre, Number(acc.$last_round_price?.value || 0));
    }, 0);

    const newData = {
      ...detailValue,
      $last_round_total: {
        value: lastRoundTotal,
        currency: detailValue?.currency || newQuotes?.[0]?.currency || 'rmb',
      },
      demand_supplier_quotes: newQuotes,
    };
    setDetailValue(newData);
    setLocalData(newData);
  });

  // 详情发生变化，获取上一轮报价
  useDeepCompareEffect(() => {
    setTimeout(() => {
      mergeLastRoundQuotes();
    }, 40);
    setTimeout(() => {
      handleChangeForm({}, form.getFieldsValue());
    }, 50);
  }, [detail]);

  return (
    <div className={cx('new-pending-quote-container')}>
      <div className={cx('new-pending-quote-container-body')}>
        <Form
          form={form}
          onValuesChange={handleChangeForm}
          initialValues={{
            service_fee_mode: 0,
          }}
        >
          <Collapse activeKey={[1]}>
            <CollapseTablePanel
              className="m-b-24"
              showArrow={false}
              key={1}
              header={<div>报价汇总</div>}
            >
              <DescriptionsFormItem
                className={cx('property_values_descriptions')}
                column={2}
                items={items as any}
              />
            </CollapseTablePanel>
          </Collapse>

          <Form.List name="demand_supplier_quotes">
            {(fields, { add, remove }, { errors }) => {
              return (
                <>
                  {tabMode && (
                    <>
                      <div className="fw-bold m-b-12">合作内容（{accounts?.length}）</div>
                      <Tabs activeKey={activeTabKey} type="card" onChange={(v) => {setActiveTabKey(v)}} className='m-b-12'>
                        {fields.map((field, index) => {
                          const account = accounts?.find((n) => n.id === detailValue?.demand_supplier_quotes?.[index]?.cooperate_account_id);
                          return (
                            <Tabs.TabPane
                              tab={(
                                <>
                                  合作内容{orderNum(account?.$orderIndex, index)}
                                  {account?.need_ai && <Tag color="orange" size="xSmall" className="m-l-4" text={'需包含AI技术'} />}
                                  {detail?.depth === 2 && (
                                    <ActualExecutionTag value={detailValue?.demand_supplier_quotes?.[index]?.need_actual_execution} className="m-l-8" />
                                  )}
                                </>
                              )}
                              key={index + ''}
                            ></Tabs.TabPane>
                          );
                        })}
                      </Tabs>
                    </>
                  )}
                  {fields.map((field, index) => {
                    const { key, name, ...fieldRest } = field;
                    const fieldConfig = (fieldType: string | string[]) => {
                      const types =
                        typeof fieldType === 'string' ? [fieldType] : fieldType;
                      const moreFields: Record<string, any> = {};
                      if (fieldType.includes('property_values')) {
                        const account =
                          accounts?.find(
                            (n) => n.id === cooperate_account_id
                          ) || {};
                        if (!account.property_values?.length) {
                          moreFields.show = false;
                        }
                      }
                      return {
                        formItemKey: [name, ...types],
                        name: [name, ...types],
                        fieldKey: [key, ...types],
                        view: false,
                        ...fieldRest,
                        ...moreFields,
                      };
                    };

                    const demand_supplier_quote =
                      detailValue?.demand_supplier_quotes?.[index] || {};
                    const { cooperate_account_id } = demand_supplier_quote;
                    const account =
                      accounts?.find((n) => n.id === cooperate_account_id) ||
                      {};
                    const { delay_grade, delay_label, $orderIndex } =
                      account || {};

                    return (
                      <Collapse
                        className={tabMode && index !== Number(activeTabKey) ? 'display-none' : ''}
                        key={index}
                        defaultActiveKey={
                          // tabmode 模式下前20也默认展开
                          !tabMode && (type === 'history' || index > 20) ? [] : [1]
                        }
                      >
                        <CollapseTablePanel
                          key={1}
                          header={
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                width: '100%',
                              }}
                            >
                              <Space size={4}>
                                <span>
                                  合作内容{orderNum($orderIndex, index)}
                                </span>
                                {account?.need_ai && <Tag color="orange" size="xSmall" text={'需包含AI技术'}></Tag>}
                                {detailValue?.demand_supplier_quotes?.[index]?.quote_contain_ai && <AiTag variant="filled" size="xSmall" text="报价含AI"></AiTag>}
                                {detail?.depth === 2 && (
                                  <ActualExecutionTag
                                    value={
                                      detailValue?.demand_supplier_quotes?.[
                                        index
                                      ]?.need_actual_execution
                                    }
                                  />
                                )}
                                <TravelTag 
                                  value={detailValue?.demand_supplier_quotes?.[
                                    index
                                  ]?.contain_travel_expenses_reimbursement}
                                  need_actual_execution={detailValue?.demand_supplier_quotes?.[
                                    index
                                  ]?.need_actual_execution}
                                  detail={detail}
                                />
                                <OvertimeTag
                                  delay_grade={delay_grade}
                                  delay_label={delay_label}
                                  className=""
                                />
                              </Space>
                              {detail?.need_cooperate_account_supplier_settle_price &&
                                type !== 'making' && (
                                  <div>
                                    <span className={cx('settle-title')}>
                                      最终结算金额：
                                    </span>
                                    <CurrencyType
                                      value={{
                                        value:
                                          demand_supplier_quote?.supplier_settle_price,
                                        currency: demand_supplier_quote?.currency,
                                      }}
                                      view
                                      showAmountOrder={false}
                                    />
                                  </div>
                                )}
                            </div>
                          }
                          className={
                            OvertimeTag.conf?.[delay_grade]
                              ?.panelHeaderbgClassName
                          }
                        >
                          <DescriptionsFormItem
                            lightBorder
                            className={cx('property_values_descriptions')}
                            items={getCooperationContentFormItem(
                              fieldConfig,
                              orderNum($orderIndex, index),
                              demand_supplier_quote,
                              account
                            ).filter((item) => {
                              const formItemKey = Array.isArray(
                                item.formItemKey
                              )
                                ? item.formItemKey[2]
                                : item.formItemKey;
                              if (detail.is_bind_execution_data) {
                                                                    return ['property_values', 'program_desc', 'remark'].includes(formItemKey)
                              } else {
                                return !['program_desc'].includes(formItemKey);
                              }
                            })}
                            column={2}
                          />
                        </CollapseTablePanel>
                      </Collapse>
                    );
                  })}
                </>
              );
            }}
          </Form.List>
        </Form>
      </div>
    </div>
  );
});

export default NewPendingQuote;
